import { FaceKeyOffset, FaceRoiTextureMetrics } from './face-3d-lab-geometry.util';

/**
 * Tipos mínimos WebXR Depth Sensing (experimental) para el lab.
 * No sustituyen lib.dom; solo cubren lo que usamos en runtime feature-detect.
 */
export type FaceDepthApiKind = 'none' | 'web-estimated' | 'webxr-depth-sensing' | 'native-bridge';

export interface FaceDepthCapabilityReport {
	secureContext: boolean;
	xrSystemPresent: boolean;
	immersiveArSupported: boolean | null;
	depthSensingSupported: boolean | null;
	depthAvailable: boolean;
	metricDepthAvailable: boolean;
	api: FaceDepthApiKind;
	detail: string;
	sessionProbe: FaceDepthSessionProbe | null;
}

export interface FaceDepthSessionProbe {
	width: number;
	height: number;
	rawValueToMeters: number;
	usage: string | null;
	format: string | null;
	sampleMeanDepthM: number | null;
	fpsEstimate: number | null;
}

export interface FaceDepthRoiSnapshot {
	meanDepthM: number;
	varianceM2: number;
	validSampleRatio: number;
	planarFitErrorM: number;
	capturedAtMs: number;
	texture?: FaceRoiTextureMetrics | null;
	/** Offsets landmarks (físicos, sin zoom) para métrica A forma. */
	keyOffsets?: FaceKeyOffset[];
}

/**
 * Lab depth-real — anti-foto far↔near (acercamiento = protocolo).
 *
 * A — forma (residual de homotecia): foto = zoom puro; cara real distorsiona.
 * B — diferencia de imagen (1−NCC) + veto duro anti-foto.
 * Protocolo: min≤delta≤max (fotos suelen inflar el acercamiento proxy).
 * Veto plano: residual < maxFlatShapeResidual → FAIL (sin bypass de apariencia).
 * Laplaciano / zDecoupling: solo diagnóstico (inestables en webcam).
 */
export interface FaceDepthRealThresholds {
	minDeltaDepthM: number;
	/** Tope de acercamiento proxy; fotos/pantalla suelen superar cara real. */
	maxDeltaDepthM: number;
	minValidSampleRatio: number;
	minShapeScore: number;
	minAppearanceScore: number;
	minAppearanceDelta: number;
	minFinalScore: number;
	fullShapeResidual: number;
	fullAppearanceDelta: number;
	/**
	 * Residual por debajo = zoom isótropo (foto). Calibración approach 2026-08-06:
	 * 0 reales < 0.02; varias fotos claras sí. Veto duro, no bypassable.
	 */
	maxFlatShapeResidual: number;
	weightShape: number;
	weightAppearance: number;
}

export interface FaceDepthRealScores {
	meanDepthFarM: number;
	meanDepthNearM: number;
	deltaDepthM: number;
	zDecoupling: number;
	/** Diagnóstico: cambio laplaciano (ya no es gate). */
	detailDelta: number;
	/** Métrica A: residual tras mejor escala far→near. */
	shapeResidual: number;
	shapeScore: number;
	appearanceDelta: number;
	appearanceScore: number;
	finalScore: number;
	gateDeltaOk: boolean;
	gateShapeOk: boolean;
	gateAppearanceOk: boolean;
	/** false si residual < maxFlatShapeResidual (firma foto plana). */
	gateFlatShapeOk: boolean;
	passed: boolean;
}

export const DEFAULT_FACE_DEPTH_REAL_THRESHOLDS: FaceDepthRealThresholds = {
	minDeltaDepthM: 0.08,
	/**
	 * Calibración lab 2026-08-06 batch2 (estimated): reales Δ≤0.34; fotos agresivas Δ≥0.35.
	 * Spoofs con Δ controlado (~0.25–0.29) aún pueden colar — hace falta señal extra.
	 */
	maxDeltaDepthM: 0.345,
	minValidSampleRatio: 0.35,
	minShapeScore: 24,
	minAppearanceScore: 40,
	/**
	 * Calibración lab 2026-08-07 (estimated, 24 real / 11 foto):
	 * FN reales con appΔ 0.128–0.147; fotos que fallan por B ≤0.12.
	 * 0.12 recupera el veto de delta en esos FN sin abrir fotos tipadas.
	 */
	minAppearanceDelta: 0.12,
	minFinalScore: 45,
	/** Residual ~0.04–0.08 cara real; foto ~0. */
	fullShapeResidual: 0.055,
	fullAppearanceDelta: 0.25,
	maxFlatShapeResidual: 0.02,
	weightShape: 0.35,
	weightAppearance: 0.65,
};

export interface FaceDepthNativeBridgePayload {
	source: 'arkit-truedepth' | 'arcore-depth' | 'camera2-tof';
	far: Omit<FaceDepthRoiSnapshot, 'capturedAtMs'> & { capturedAtMs?: number };
	near: Omit<FaceDepthRoiSnapshot, 'capturedAtMs'> & { capturedAtMs?: number };
}
